import cx from 'clsx';
import React from 'react';
import { Button } from 'ui/component/Button.tsx';
import { IChevronDown } from 'ui/component/Icons.tsx';

export type TreeNode<NodeDataType> = {
  data: NodeDataType;
  children: TreeNode<NodeDataType>[];
};

export type NodeState = {
  expanded: boolean;
};

export type NodesState = Record<string, NodeState>;

type Props<NodeType> = {
  nodes: TreeNode<NodeType>[];
  renderNode: (node: TreeNode<NodeType>) => React.ReactNode;
  getNodeKey: (node: TreeNode<NodeType>) => string;
  nodesState: NodesState;
  onNodeStateChange: (nodeKey: string, nodeState: NodeState) => void;
  className?: string;
};

export const Tree = <NodeType,>({ nodes, renderNode, getNodeKey, nodesState, onNodeStateChange, className }: Props<NodeType>) => {
  const toggleNode = React.useCallback(
    (nodeKey: string) => {
      onNodeStateChange(nodeKey, { expanded: !nodesState[nodeKey]?.expanded });
    },
    [nodesState, onNodeStateChange],
  );

  return (
    <ul className={cx('list-none', className, 'flex w-full flex-col gap-3')}>
      {nodes.map((node) => {
        const renderedNode = renderNode(node);
        if (!renderedNode) return null;

        return (
          <li key={getNodeKey(node)} className="flex w-full flex-col">
            <div className="flex flex-row items-start gap-3">
              <Button
                small
                className={cx(
                  'h-8! w-8! min-w-8! border border-grey-border bg-white p-0! text-grey transition-all hover:border-blue hover:bg-blue-light hover:text-blue',
                  node.children.length === 0 && 'invisible',
                )}
                disabled={node.children.length === 0}
                onClick={() => toggleNode(getNodeKey(node))}
              >
                <IChevronDown className={cx(nodesState[getNodeKey(node)]?.expanded && 'rotate-180', 'transition-transform duration-200')} />
              </Button>

              {renderNode(node)}
            </div>
            {node.children.length > 0 && (
              <div
                className={cx(
                  'ml-11 grid border-grey-border border-l-2 pl-6 transition-all duration-300 ease-in-out',
                  nodesState[getNodeKey(node)]?.expanded ? 'mt-3 grid-rows-[1fr] opacity-100' : 'mt-0 grid-rows-[0fr] opacity-0',
                )}
              >
                <div className="overflow-hidden">
                  <Tree nodes={node.children} renderNode={renderNode} getNodeKey={getNodeKey} nodesState={nodesState} onNodeStateChange={onNodeStateChange} />
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};
